// FOTO_CANTIERE_v1 — fotocamera a raffica del cantiere
// FOTO_CANTIERE_v2 — nome breve (vista operaio) nella tendina
// Le foto vanno sul NAS come file (cartella del cantiere / foto / lavorazione),
// nel database resta solo una registrazione leggera in "assenze" (tipo "foto").
import React, { useState, useEffect, useRef } from "react";

const C = {
  bg: "#0f1117", surface: "#1a1d27", border: "#2a2e45", accent: "#f5a623",
  text: "#e8eaf2", muted: "#7b82a0", dim: "#4a5068", ok: "#3ecf8e", err: "#f06060",
};

const MAX_LATO = 1920;
const QUALITA = 0.8;
const MINI_LATO = 320;

function tok() {
  try { return localStorage.getItem("bertoc_jwt") || ""; } catch (e) { return ""; }
}

function norm(t) {
  return String(t || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
}

const NUMERI = { uno:"1", due:"2", tre:"3", quattro:"4", cinque:"5", sei:"6", sette:"7", otto:"8", nove:"9", dieci:"10",
  undici:"11", dodici:"12", tredici:"13", quattordici:"14", quindici:"15", sedici:"16", diciassette:"17", diciotto:"18",
  diciannove:"19", venti:"20" };

// Punteggio di somiglianza tra quello che si e' detto/scritto e una lavorazione
function punteggio(q, voce) {
  const parole = norm(q).split(" ").filter(Boolean).map(p => NUMERI[p] || p);
  if (!parole.length) return 0;
  const testo = norm(voce.desc + " " + (voce.breve || "") + " " + (voce.gruppo || ""));
  const numero = norm(voce.numVoce);
  let s = 0;
  for (let k = 0; k < parole.length; k++) {
    const p = parole[k];
    if (/^\d+$/.test(p)) {
      const prima = k > 0 ? parole[k - 1] : "";
      if (numero && p === numero) s += (prima === "voce" || prima === "numero" || parole.length === 1) ? 10 : 4;
      continue;
    }
    if (p.length < 3 || p === "voce" || p === "numero") continue;
    if ((" " + testo + " ").indexOf(" " + p + " ") >= 0) s += 3;
    else if (testo.indexOf(p) >= 0) s += 2;
    else if (p.length >= 5 && testo.indexOf(p.slice(0, p.length - 1)) >= 0) s += 1.5;
  }
  return s;
}

function caricaImg(src) {
  return new Promise((ok, ko) => { const i = new Image(); i.onload = () => ok(i); i.onerror = ko; i.src = src; });
}

function ridimensiona(sorgente, w, h, lato, q) {
  let W = w, H = h;
  if (Math.max(W, H) > lato) { const r = lato / Math.max(W, H); W = Math.round(W * r); H = Math.round(H * r); }
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  cv.getContext("2d").drawImage(sorgente, 0, 0, W, H);
  return new Promise(ok => cv.toBlob(b => ok(b), "image/jpeg", q));
}

async function preparaDaSorgente(sorgente, w, h) {
  const full = await ridimensiona(sorgente, w, h, MAX_LATO, QUALITA);
  const piccola = await ridimensiona(sorgente, w, h, 1280, 0.7);
  const mini = await ridimensiona(sorgente, w, h, MINI_LATO, 0.7);
  return { full, piccola, mini };
}

async function preparaDaFile(file) {
  const u = URL.createObjectURL(file);
  try {
    const img = await caricaImg(u);
    return await preparaDaSorgente(img, img.naturalWidth, img.naturalHeight);
  } finally { URL.revokeObjectURL(u); }
}

async function invia(item, usaPiccola) {
  const fd = new FormData();
  fd.append("cantiereId", item.cantiereId);
  fd.append("cantiere", item.cantiere || "");
  fd.append("lavorazione", item.lav ? item.lav.desc || "" : "");
  fd.append("lavorazioneIdx", item.lav ? String(item.lav.i) : "");
  fd.append("lavorazioneId", item.lav && item.lav.id != null ? String(item.lav.id) : "");
  fd.append("numVoce", item.lav ? String(item.lav.numVoce || "") : "");
  fd.append("scattataIl", item.quando);
  fd.append("foto", usaPiccola ? item.piccola : item.full, "foto.jpg");
  fd.append("mini", item.mini, "mini.jpg");
  const r = await fetch("/api/foto-cantiere/carica", { method: "POST", headers: { Authorization: "Bearer " + tok() }, body: fd });
  if (!r.ok) {
    const t = await r.text().catch(() => "");
    const e = new Error("HTTP " + r.status + " " + t.slice(0, 120));
    e.status = r.status;
    throw e;
  }
  return r.json();
}

export function fotoCantiereElimina(f) {
  try {
    if (!f || !f.file || !f.cid) return;
    fetch("/api/foto-cantiere/elimina", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + tok() },
      body: JSON.stringify({ cantiereId: f.cid, file: f.file }),
    }).catch(() => {});
  } catch (e) {}
}

// ---------------------------------------------------------------------------
// Fotocamera a schermo intero
// ---------------------------------------------------------------------------
function FotoCamera({ cant, onClose, operaio }) {
  const cid = cant._id || cant.id;
  const voci = (cant.lavorazioni || []).map((l, i) => ({ i, id: l.id, numVoce: l.numVoce || "", desc: l.desc || "(senza nome)", breve: String(l.descBreve || "").trim(), gruppo: l.gruppo || "" }));
  const nomeDi = v => (v && (v.breve || v.desc)) || "";
  const chiaveUltima = "foto_cant_lav_" + cid;

  const [sel, setSel] = useState(() => {
    try {
      const v = localStorage.getItem(chiaveUltima);
      if (v === "g") return "g";
      if (v != null && voci.some(x => String(x.i) === v)) return parseInt(v, 10);
    } catch (e) {}
    return null;
  });
  const [lista, setLista] = useState(sel === null);
  const [cerca, setCerca] = useState("");
  const [ascolto, setAscolto] = useState(false);
  const [camOk, setCamOk] = useState(null);
  const [camErr, setCamErr] = useState("");
  const [lampo, setLampo] = useState(false);
  const [coda, setCoda] = useState([]);
  const [ingrandita, setIngrandita] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const codaRef = useRef([]);
  const lavoraRef = useRef(false);
  const riconRef = useRef(null);
  const fileRef = useRef(null);
  const vivoRef = useRef(true);

  const voceSel = sel === "g" ? null : voci.find(v => v.i === sel) || null;
  const etichetta = sel === "g" ? "Generale (senza lavorazione)"
    : voceSel ? ((voceSel.numVoce ? voceSel.numVoce + " · " : "") + nomeDi(voceSel)) : "Scegli la lavorazione";

  const aggiorna = () => setCoda(codaRef.current.slice());

  useEffect(() => {
    vivoRef.current = true;
    const prima = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    avviaCamera();
    return () => {
      vivoRef.current = false;
      document.body.style.overflow = prima;
      fermaCamera();
      try { if (riconRef.current) riconRef.current.abort(); } catch (e) {}
      codaRef.current.forEach(x => { try { URL.revokeObjectURL(x.anteprima); } catch (e) {} });
    };
  }, []);

  useEffect(() => {
    try { if (sel !== null) localStorage.setItem(chiaveUltima, String(sel)); } catch (e) {}
  }, [sel, chiaveUltima]);

  async function avviaCamera() {
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      setCamOk(false);
      setCamErr(window.isSecureContext === false
        ? "Da questo indirizzo il telefono non lascia usare la fotocamera nella pagina. Apri il gestionale da https://bertocproject.synology.me oppure usa «Dalla galleria»."
        : "Questo browser non lascia usare la fotocamera nella pagina. Usa «Dalla galleria».");
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" }, width: { ideal: 3840 }, height: { ideal: 2160 } },
      });
      if (!vivoRef.current) { s.getTracks().forEach(t => t.stop()); return; }
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        try { await videoRef.current.play(); } catch (e) {}
      }
      setCamOk(true);
    } catch (e) {
      setCamOk(false);
      setCamErr(e && e.name === "NotAllowedError"
        ? "Permesso fotocamera negato. Su iPhone: aA nella barra dell'indirizzo → Impostazioni sito web → Fotocamera → Consenti, poi riapri."
        : "Fotocamera non disponibile (" + ((e && e.name) || "errore") + "). Usa «Dalla galleria».");
    }
  }

  function fermaCamera() {
    try { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) {}
    streamRef.current = null;
  }

  function accoda(blobs) {
    const item = {
      k: Date.now() + "_" + Math.random().toString(36).slice(2, 7),
      cantiereId: cid, cantiere: cant.nome || "",
      lav: voceSel ? { ...voceSel } : null,
      quando: new Date().toISOString(),
      full: blobs.full, piccola: blobs.piccola, mini: blobs.mini,
      anteprima: URL.createObjectURL(blobs.mini),
      stato: "attesa", errore: "",
    };
    codaRef.current = codaRef.current.concat(item);
    aggiorna();
    lavora();
  }

  async function lavora() {
    if (lavoraRef.current) return;
    lavoraRef.current = true;
    try {
      for (;;) {
        const item = codaRef.current.find(x => x.stato === "attesa");
        if (!item) break;
        item.stato = "invio"; aggiorna();
        try {
          let ris;
          try { ris = await invia(item, false); }
          catch (e) {
            if (e.status === 413) ris = await invia(item, true);
            else throw e;
          }
          item.stato = "ok"; item.ris = ris;
          item.full = null; item.piccola = null;
        } catch (e) {
          item.stato = "errore"; item.errore = (e && e.message) || "errore";
          if (e && (e.status === 401 || e.status === 403)) item.errore = "Accesso scaduto: esci e rientra nel gestionale";
        }
        aggiorna();
        if (!vivoRef.current) break;
      }
    } finally { lavoraRef.current = false; }
    if (vivoRef.current && codaRef.current.some(x => x.stato === "errore")) {
      setTimeout(() => {
        if (!vivoRef.current) return;
        codaRef.current.forEach(x => { if (x.stato === "errore" && !/Accesso/.test(x.errore)) x.stato = "attesa"; });
        aggiorna();
        lavora();
      }, 8000);
    }
  }

  async function scatta() {
    if (sel === null) { setLista(true); return; }
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    setLampo(true);
    setTimeout(() => setLampo(false), 120);
    const blobs = await preparaDaSorgente(v, v.videoWidth, v.videoHeight);
    accoda(blobs);
  }

  async function daGalleria(e) {
    const files = Array.from((e.target && e.target.files) || []);
    e.target.value = "";
    if (sel === null) { setLista(true); return; }
    for (const f of files) {
      try { accoda(await preparaDaFile(f)); }
      catch (err) { alert("Non riesco a leggere " + (f.name || "la foto")); }
    }
  }

  function chiudi() {
    const pendenti = codaRef.current.filter(x => x.stato !== "ok").length;
    if (pendenti > 0 && !window.confirm(pendenti + (pendenti === 1 ? " foto non e' ancora arrivata" : " foto non sono ancora arrivate") + " sul NAS. Se chiudi adesso si perdono. Chiudere lo stesso?")) return;
    onClose();
  }

  const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

  function parla() {
    if (!SR) return;
    if (ascolto) { try { riconRef.current && riconRef.current.stop(); } catch (e) {} return; }
    try {
      const r = new SR();
      r.lang = "it-IT";
      r.interimResults = true;
      r.maxAlternatives = 1;
      r.continuous = false;
      r.onresult = ev => {
        let t = "";
        for (let k = 0; k < ev.results.length; k++) t += ev.results[k][0].transcript;
        setCerca(t);
        const ultimo = ev.results[ev.results.length - 1];
        if (ultimo && ultimo.isFinal) scegliMigliore(t);
      };
      r.onend = () => setAscolto(false);
      r.onerror = () => setAscolto(false);
      riconRef.current = r;
      setLista(true);
      setCerca("");
      setAscolto(true);
      r.start();
    } catch (e) { setAscolto(false); }
  }

  function scegliMigliore(t) {
    if (/general/.test(norm(t))) { setSel("g"); setLista(false); setCerca(""); return; }
    const ord = voci.map(v => ({ v, s: punteggio(t, v) })).filter(x => x.s > 0).sort((a, b) => b.s - a.s);
    if (ord.length && (ord.length === 1 || ord[0].s >= ord[1].s + 2)) {
      setSel(ord[0].v.i); setLista(false); setCerca("");
    }
  }

  const filtrate = cerca.trim()
    ? voci.map(v => ({ v, s: punteggio(cerca, v) })).filter(x => x.s > 0).sort((a, b) => b.s - a.s).map(x => x.v)
    : voci;

  const nOk = coda.filter(x => x.stato === "ok").length;
  const nAtt = coda.filter(x => x.stato === "attesa" || x.stato === "invio").length;
  const nErr = coda.filter(x => x.stato === "errore").length;
  const sessioneLav = coda.filter(x => (x.lav ? x.lav.i : "g") === sel).length;

  const btnTondo = { width: 48, height: 48, borderRadius: 24, border: "none", background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "#000", color: C.text, display: "flex", flexDirection: "column", fontFamily: "'DM Sans',sans-serif", WebkitUserSelect: "none", userSelect: "none" }}>
      <div style={{ padding: "calc(env(safe-area-inset-top, 0px) + 10px) 12px 10px", background: C.bg, borderBottom: "1px solid " + C.border, display: "flex", alignItems: "center", gap: 8 }}>
        <button onClick={chiudi} style={{ ...btnTondo, width: 40, height: 40, background: C.surface, fontSize: 18 }} aria-label="Chiudi">✕</button>
        <button onClick={() => setLista(!lista)} style={{ flex: 1, minWidth: 0, textAlign: "left", padding: "9px 12px", borderRadius: 10, border: "1px solid " + (sel === null ? C.accent : C.border), background: C.surface, color: sel === null ? C.accent : C.text, fontSize: 15, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{etichetta}</span>
          <span style={{ color: C.muted, fontSize: 12 }}>{lista ? "▲" : "▼"}</span>
        </button>
        {SR && (
          <button onClick={parla} style={{ ...btnTondo, width: 40, height: 40, background: ascolto ? C.err : C.surface, fontSize: 18 }} aria-label="Dillo a voce">🎤</button>
        )}
      </div>

      {lista && (
        <div style={{ position: "absolute", top: "calc(env(safe-area-inset-top, 0px) + 62px)", left: 0, right: 0, bottom: 0, zIndex: 3, background: C.bg, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: 12, borderBottom: "1px solid " + C.border }}>
            <input value={cerca} onChange={e => setCerca(e.target.value)} placeholder={ascolto ? "Ti ascolto…" : "Cerca o detta la lavorazione"}
              style={{ width: "100%", padding: "12px 14px", fontSize: 16, boxSizing: "border-box", borderRadius: 10, border: "1px solid " + (ascolto ? C.err : C.border), background: C.surface, color: C.text, outline: "none" }} />
            {ascolto && <div style={{ fontSize: 12, color: C.err, marginTop: 6 }}>● Parla adesso: per esempio «intonaco» oppure «voce 5»</div>}
          </div>
          <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
            {!cerca.trim() && (
              <div onClick={() => { setSel("g"); setLista(false); setCerca(""); }}
                style={{ padding: "14px 16px", borderBottom: "1px solid " + C.border + "88", color: sel === "g" ? C.accent : C.muted, fontSize: 15, cursor: "pointer" }}>
                📁 Generale (senza lavorazione)
              </div>
            )}
            {filtrate.map(v => (
              <div key={v.i} onClick={() => { setSel(v.i); setLista(false); setCerca(""); }}
                style={{ padding: "13px 16px", borderBottom: "1px solid " + C.border + "88", display: "flex", gap: 10, alignItems: "baseline", cursor: "pointer", background: sel === v.i ? "#f5a62318" : "transparent" }}>
                <span style={{ minWidth: 30, fontSize: 12, fontWeight: 700, color: C.accent }}>{v.numVoce || "#" + (v.i + 1)}</span>
                <span style={{ flex: 1, fontSize: 15, color: sel === v.i ? C.accent : C.text }}>
                  <span style={{ fontWeight: v.breve && !operaio ? 600 : 400 }}>{nomeDi(v)}</span>
                  {v.gruppo ? <span style={{ fontSize: 12, color: C.muted }}>{"  · " + v.gruppo}</span> : null}
                  {v.breve && !operaio ? <span style={{ display: "block", fontSize: 12, color: C.muted, marginTop: 2 }}>{v.desc}</span> : null}
                </span>
              </div>
            ))}
            {cerca.trim() && filtrate.length === 0 && (
              <div style={{ padding: 20, color: C.muted, fontSize: 14 }}>Nessuna lavorazione trovata per «{cerca}».</div>
            )}
            {voci.length === 0 && (
              <div style={{ padding: 20, color: C.muted, fontSize: 14 }}>Questo cantiere non ha lavorazioni: le foto vanno in «Generale».</div>
            )}
          </div>
        </div>
      )}

      <div style={{ flex: 1, position: "relative", overflow: "hidden", background: "#000" }}>
        <video ref={videoRef} playsInline muted autoPlay style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: camOk ? "block" : "none" }} />
        {camOk === null && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>Apro la fotocamera…</div>}
        {camOk === false && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 28, textAlign: "center", color: C.muted, fontSize: 15, lineHeight: 1.5 }}>{camErr}</div>
        )}
        {lampo && <div style={{ position: "absolute", inset: 0, background: "#fff", opacity: 0.7 }} />}
        {sel !== null && sessioneLav > 0 && (
          <div style={{ position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.6)", padding: "5px 12px", borderRadius: 14, fontSize: 13 }}>
            {sessioneLav} foto su questa lavorazione
          </div>
        )}
      </div>

      <div style={{ background: C.bg, borderTop: "1px solid " + C.border, padding: "8px 12px calc(env(safe-area-inset-bottom, 0px) + 12px)" }}>
        <div style={{ display: "flex", gap: 6, overflowX: "auto", minHeight: 50, alignItems: "center", marginBottom: 8 }}>
          {coda.slice().reverse().map(x => (
            <div key={x.k} onClick={() => setIngrandita(x)} style={{ position: "relative", flexShrink: 0 }}>
              <img src={x.anteprima} alt="" style={{ width: 46, height: 46, objectFit: "cover", borderRadius: 7, border: "2px solid " + (x.stato === "ok" ? C.ok : x.stato === "errore" ? C.err : C.border), opacity: x.stato === "ok" ? 1 : 0.6 }} />
              <span style={{ position: "absolute", bottom: 2, right: 3, fontSize: 11 }}>{x.stato === "ok" ? "✓" : x.stato === "errore" ? "!" : "…"}</span>
            </div>
          ))}
          {coda.length === 0 && <span style={{ fontSize: 13, color: C.dim }}>Le foto scattate compaiono qui e partono da sole verso il NAS.</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <label style={{ ...btnTondo, background: C.surface, fontSize: 20 }} aria-label="Dalla galleria">
            🖼️
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={daGalleria} style={{ display: "none" }} />
          </label>
          <button onClick={scatta} disabled={!camOk && sel !== null} aria-label="Scatta"
            style={{ width: 76, height: 76, borderRadius: 38, border: "4px solid " + (sel === null ? C.accent : "#fff"), background: camOk ? "#fff" : C.dim, boxShadow: "0 0 0 3px #000 inset", cursor: "pointer", opacity: camOk || sel === null ? 1 : 0.4 }} />
          <div style={{ width: 64, textAlign: "right", fontSize: 12, lineHeight: 1.35 }}>
            <div style={{ color: C.ok }}>{nOk} ✓</div>
            {nAtt > 0 && <div style={{ color: C.muted }}>{nAtt} in invio</div>}
            {nErr > 0 && <div style={{ color: C.err }}>{nErr} in attesa di rete</div>}
          </div>
        </div>
      </div>

      {ingrandita && (
        <div onClick={() => setIngrandita(null)} style={{ position: "absolute", inset: 0, zIndex: 5, background: "rgba(0,0,0,0.92)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <img src={ingrandita.anteprima} alt="" style={{ maxWidth: "100%", maxHeight: "75%", objectFit: "contain", borderRadius: 8 }} />
          <div style={{ marginTop: 12, fontSize: 14, color: C.text, textAlign: "center" }}>
            {ingrandita.lav ? ((ingrandita.lav.numVoce ? ingrandita.lav.numVoce + " · " : "") + nomeDi(ingrandita.lav)) : "Generale"}
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: ingrandita.stato === "ok" ? C.ok : ingrandita.stato === "errore" ? C.err : C.muted }}>
            {ingrandita.stato === "ok" ? "Arrivata sul NAS" : ingrandita.stato === "errore" ? "Non ancora arrivata: riprovo da solo (" + ingrandita.errore + ")" : "In invio…"}
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: C.dim }}>Tocca per chiudere</div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pulsante (e riquadro) da mettere nella scheda cantiere e nella vista operaio
// ---------------------------------------------------------------------------
export default function FotoCantiereBtn({ cant, assenze, grande }) {
  const [aperta, setAperta] = useState(false);
  if (!cant) return null;
  const cid = cant._id || cant.id;
  const foto = (assenze || []).filter(a => a && a.tipo === "foto" && a.cantiereId === cid);
  const tutte = foto.flatMap(a => (a.foto || []).map(f => ({ ...f, _a: a })));
  const generali = tutte.filter(f => !f._a.lavorazioneId && (f._a.lavorazioneIdx == null || f._a.lavorazioneIdx === "") && !f._a.lavorazione);

  const bottone = (
    <button onClick={() => setAperta(true)}
      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: grande ? "100%" : "auto", padding: grande ? "16px 18px" : "10px 16px", borderRadius: 12, border: "1px solid " + C.accent, background: "#f5a62322", color: C.accent, fontSize: grande ? 17 : 14, fontWeight: 700, cursor: "pointer" }}>
      📷 Foto cantiere
    </button>
  );

  return (
    <>
      {grande ? (
        <div style={{ marginBottom: 16 }}>{bottone}</div>
      ) : (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 14 }}>📷 Foto del cantiere</div>
              <div style={{ fontSize: 12, color: C.muted }}>
                {tutte.length ? tutte.length + " foto" : "Nessuna foto ancora"} · scegli la lavorazione e scatta quante foto vuoi
              </div>
            </div>
            {bottone}
          </div>
          {generali.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Generali</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {generali.slice(-24).reverse().map((f, k) => (
                  <img key={k} src={f.url} alt="" onClick={() => { const u = f.full || f.url; if (u) window.open(u, "_blank"); }}
                    style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid " + C.border, cursor: "pointer" }} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {aperta && <FotoCamera cant={cant} operaio={!!grande} onClose={() => setAperta(false)} />}
    </>
  );
}
